/**
 * 관리 대시보드 — 사이트 전반 요약(사용자/서비스/메뉴/아이콘/외부연결/AI). 타일 클릭 시 해당 메뉴 이동.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import './ServiceModules.css';
import {
  Users, Settings, LayoutGrid, Palette, Globe, Sparkles, Bot, Building, RefreshCw,
} from '../../../icons';
import { getDashboard, type DashboardData } from '../../../api/dashboard';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

const ROLE_LABEL: Record<string, string> = {
  SYSTEM_ADMIN: '시스템관리자', ADMIN: '관리자', MANAGER: '매니저', STAFF: '직원', AI_BOT: 'AI봇',
};
const MODULE_LABEL: Record<string, string> = {
  mail: '메일', calendar: '캘린더', task: '할일', workspace: '워크스페이스', workchat: '채팅',
};

/**
 * `opsAllowed=false` 면 **운영자 전용 탭으로 가는 바로가기를 끊는다**(숫자는 그대로 보여 준다).
 * 눌러도 못 가는 카드를 남기면 "왜 안 되지" 가 남는다 — 공휴일 잠금에서 겪은 것과 같은 자리.
 */
export function DashboardSection({ onNavigate, opsAllowed = true }: { onNavigate?: (tab: string) => void; opsAllowed?: boolean }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getDashboard()); }
    catch (e: any) { setError(e?.response?.data?.message ?? e?.message ?? '대시보드 로드 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const go = (tab: string) => () => onNavigate?.(tab);

  return (
    <div className="svc">
      <ConsoleScreenHead
        title={data?.institution?.name ? `${data.institution.name} 관리 대시보드` : '관리 대시보드'}
        kpi={data?.institution?.code ? `회사 코드 ${data.institution.code}` : undefined}
        desc="사이트 전반 현황 요약."
        actions={(
          <button type="button" className="svc-btn" onClick={() => void reload()} disabled={loading}>
            <RefreshCw size={13} /> 새로고침
          </button>
        )}
      />

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {loading && !data ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : (
        <>
          {/* 통계 타일 */}
          <div className="svc-dash-grid">
            {/* 2026-08-25 — '사용자' 타일은 뺐다(사용자 지시). 사용자 관리가 콘솔에서 빠져
                갈 곳이 없어졌고, 인원 수는 아래 역할 분포 카드가 이미 말해 준다. */}
            <StatTile icon={<Settings size={20} />} accent="#0ea5e9" label="활성 서비스"
              value={data?.modules ? `${data.modules.enabled}/${data.modules.total}` : '—'} sub="내장 모듈" onClick={go('modules')} />
            <StatTile icon={<LayoutGrid size={20} />} accent="#f59e0b" label="메뉴"
              value={data?.menus ? `${data.menus.enabled}/${data.menus.total}` : '—'} sub="노출/전체" onClick={go('menu')} />
            <StatTile icon={<Palette size={20} />} accent="#ec4899" label="커스텀 아이콘"
              value={data?.icons?.total ?? '—'} sub="등록 아이콘" onClick={go('icon')} />
            <StatTile icon={<Globe size={20} />} accent="#8b5cf6" label="외부 연결"
              value={data?.externalServices ? `${data.externalServices.enabled}/${data.externalServices.total}` : '—'} sub="외부 서비스" onClick={go('modules')} />
            {/* ★ AI 요약은 **운영자에게만** — 서버 전역 값이라 회사 화면에 둘 것이 아니다(서버도 안 준다). */}
            {opsAllowed && (
              <StatTile icon={<Sparkles size={20} />} accent="#14b8a6" label="AI 도구"
                value={data?.aiTools?.total ?? '—'} sub={data?.aiTools ? `${data.aiTools.domains}개 도메인` : ''} onClick={go('ai-tools')} />
            )}
          </div>

          {/* AI Provider 상태 + 사용자 역할 분포 */}
          <div className="svc-dash-row">
            {opsAllowed && (
            <button type="button" className="svc-card svc-dash-wide" onClick={go('ai-provider')}>
              <div className="svc-card__top">
                <span className="svc-card__icon" style={{ background: 'rgba(59,130,246,.13)', color: '#3b82f6' }}><Bot size={20} /></span>
                <span className="svc-card__name">AI Provider</span>
              </div>
              {data?.aiProvider?.activeId ? (
                <>
                  <div className="svc-dash-big">{data.aiProvider.name ?? data.aiProvider.activeId}</div>
                  <div className="svc-card__chips">
                    {data.aiProvider.model && <span className="svc-card__chip">모델 {data.aiProvider.model}</span>}
                    <span className="svc-card__chip">env {data.aiProvider.envCount}</span>
                    <span className="svc-card__chip">cli {data.aiProvider.cliCount}</span>
                  </div>
                </>
              ) : (
                <div className="svc-dash-big svc-dash-big--muted">미설정 (자동 fallback)</div>
              )}
            </button>
            )}

            <div className="svc-card svc-dash-wide">
              <div className="svc-card__top">
                <span className="svc-card__icon" style={{ background: 'rgba(99,102,241,.13)', color: '#6366f1' }}><Users size={20} /></span>
                <span className="svc-card__name">사용자 역할 분포</span>
              </div>
              <div className="svc-card__chips">
                {data?.users?.byRole && Object.keys(data.users.byRole).length > 0
                  ? Object.entries(data.users.byRole).map(([role, n]) => (
                      <span key={role} className="svc-card__chip">{ROLE_LABEL[role] ?? role} {n}</span>
                    ))
                  : <span className="svc-empty">데이터 없음</span>}
              </div>
            </div>
          </div>

          {/* 내장 서비스 모듈 상태 */}
          <div className="svc-card svc-dash-wide" style={{ marginTop: 12 }}>
            <div className="svc-card__top">
              <span className="svc-card__icon" style={{ background: 'rgba(14,165,233,.13)', color: '#0ea5e9' }}><Building size={20} /></span>
              <span className="svc-card__name">내장 서비스 상태</span>
              <button type="button" className="svc-btn" style={{ marginLeft: 'auto' }} onClick={go('modules')}>관리</button>
            </div>
            <div className="svc-card__chips">
              {data?.modules?.items.map((m) => (
                <span key={m.key} className="svc-dash-modchip">
                  <span className={`svc-dot ${m.enabled ? 'svc-dot--on' : 'svc-dot--off'}`} />
                  {MODULE_LABEL[m.key] ?? m.key}
                </span>
              )) ?? <span className="svc-empty">데이터 없음</span>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatTile({ icon, accent, label, value, sub, onClick }: {
  icon: ReactNode; accent: string; label: string; value: ReactNode; sub?: string; onClick?: () => void;
}) {
  return (
    <button type="button" className="svc-dash-tile" onClick={onClick} style={{ ['--cat' as string]: accent }}>
      <span className="svc-dash-tile__icon" style={{ background: `${accent}22`, color: accent }}>{icon}</span>
      <span className="svc-dash-tile__value">{value}</span>
      <span className="svc-dash-tile__label">{label}</span>
      {sub && <span className="svc-dash-tile__sub">{sub}</span>}
    </button>
  );
}
